import React from 'react';
import { STORAGE_KEYS } from './utils';
import './SettingsModal.css';

// Accept currentGame and setShowModeLockedModal as props
function SettingsModal({
  open,
  gameOn,
  currentGame,
  hardMode,
  onHardModeChange,
  darkMode,
  setDarkMode,
  animationMode,
  setAnimationMode,
  goldMedalCount = 0,
  isProdPlay = false,
  onClose
}) {
  if (!open) return null;

  // Determine if the hard mode toggle should be disabled (mode is locked)
  // It is locked if currentGame exists and has at least one guess in the first level.
  const firstLevelData = currentGame?.levels?.[0]; // Safely access the first level
  const isModeLocked = (firstLevelData?.guesses?.length || 0) > 0; // Use a clearer name for the condition

  // This handler now *only* runs when a change is permitted (input is not disabled)
  const handleHardModeChange = (e) => {
    onHardModeChange(e.target.checked); // setHardMode expects a boolean directly
  };

  // This handler runs on click of the label
  const handleLabelClick = (e) => {
    if (isModeLocked) {
      // If the mode is locked, prevent the label from trying to toggle the checkbox
      e.preventDefault();
    }
    // If mode is NOT locked, do nothing here. The click will propagate to the input
    // and trigger the handleHardModeChange via the input's onChange.
  };

  // Determine the current text state based on the hardMode prop
  const hardModeStatusText = hardMode ? 'On' : 'Off';

  const handleAnimationChange = (e) => {
    const newMode = e.target.value;
    setAnimationMode(newMode);
    localStorage.setItem(STORAGE_KEYS.settings.animationMode, newMode);
  };

  const showTetris = !isProdPlay || goldMedalCount >= 10;
  const showMatrix = !isProdPlay || goldMedalCount >= 25;

  return (
    <div className="modal-overlay">
      <div className="settings-modal-content">
        <button className="modal-close" onClick={onClose} aria-label="Close">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            height="24"
            viewBox="0 0 24 24"
            width="24"
            fill="currentColor"
          >
            <path d="M0 0h24v24H0V0z" fill="none" />
            <path d="M18.3 5.71a1 1 0 00-1.41 0L12 10.59 7.11 5.7A1 1 0 105.7 7.11L10.59 12l-4.89 4.89a1 1 0 101.41 1.41L12 13.41l4.89 4.89a1 1 0 001.41-1.41L13.41 12l4.89-4.89a1 1 0 000-1.4z" />
          </svg>
        </button>

        <h2>Settings</h2>

        {/* Hard Mode Toggle */}
        <div className="toggle-container">
          <div className="settings-left">
            {/* Display status text */}
            <div className="setting-label">Hard Mode: {hardModeStatusText}</div>
            <div className="settings-subtext">Revealed digits must be reused</div>
            {/* Optional: Add message if locked */}
            {isModeLocked && ( // Use isModeLocked here
              <div className="settings-subtext mode-locked">
                (Mode locked after first guess)
              </div>
            )}
          </div>
          <div className="settings-icons">
            <span className="toggle-icon">🎈</span>
            {/* Add the onClick handler to the label */}
            <label className={`switch ${isModeLocked ? 'disabled' : ''}`} onClick={handleLabelClick}> {/* Add onClick here */}
              <input
                type="checkbox"
                checked={hardMode === true}
                onChange={handleHardModeChange} // onChange handles the state update when *not* disabled
                disabled={isModeLocked} // <-- Keep disabled attribute!
              />
              <span className="slider round"></span>
            </label>
            <span className="toggle-icon">🕵️‍♂️</span>
          </div>
        </div>

        {/* Dark Mode Toggle (no change needed) */}
        <div className="toggle-container">
          <div className="settings-left">
            <div className="setting-label">Dark Mode: {darkMode ? 'On' : 'Off'}</div>
          </div>
          <div className="settings-icons">
            <span className="toggle-icon">🌞</span>
            <label className="switch">
              <input
                type="checkbox"
                checked={darkMode}
                onChange={(e) => setDarkMode(e.target.checked)}
              />
              <span className="slider round"></span>
            </label>
            <span className="toggle-icon">🌜</span>
          </div>
        </div>

        {/* Reveal Style Setting */}
        <div className="toggle-container">
          <div className="settings-left">
            <div className="setting-label">Reveal Style</div>
            <div className="settings-subtext">Choose your animation style</div>
          </div>
          <div className="settings-icons">
            <select
              value={animationMode}
              onChange={handleAnimationChange}
              className="settings-select"
              style={{ padding: '5px', fontSize: '16px', borderRadius: '4px' }}
            >
              <option value="slide">Slider</option>
              {showTetris && <option value="tetris">Tetris</option>}
              {showMatrix && <option value="matrix">Matrix</option>}
            </select>
          </div>
        </div>

      </div>
    </div>
  );
}

export default SettingsModal;
